/**
 * FraudAlertsPage — `/financial-services/fraud`.
 *
 * Slice 9: wired to real endpoints:
 *   GET  /v1/financial-services/fraud-alerts
 *   POST /v1/financial-services/fraud-alerts/:id/disposition
 *
 * Operator sees the queue of NEW alerts and can disposition each one
 * (REVIEWED / DISMISSED / CONFIRMED).
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, ShieldAlert, XCircle } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  type FraudAlert,
  type FraudAlertStatus,
  dispositionFraudAlert,
  listFraudAlerts,
} from '../../lib/api/financial-services';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

const STATUS_TABS: readonly { value: FraudAlertStatus | ''; label: string }[] = [
  { value: 'NEW',        label: 'New' },
  { value: 'REVIEWED',   label: 'Reviewed' },
  { value: 'CONFIRMED',  label: 'Confirmed' },
  { value: 'DISMISSED',  label: 'Dismissed' },
  { value: '',           label: 'All' },
];

const SEVERITY_COLOURS: Readonly<Record<string, string>> = {
  CRITICAL: 'bg-red-100 text-red-800 border-red-200',
  HIGH:     'bg-orange-100 text-orange-800 border-orange-200',
  MEDIUM:   'bg-yellow-100 text-yellow-800 border-yellow-200',
  LOW:      'bg-gray-100 text-gray-700 border-gray-200',
};

function SeverityBadge({ severity }: { readonly severity: string }): JSX.Element {
  const cls = SEVERITY_COLOURS[severity] ?? 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cls}`}>
      {severity}
    </span>
  );
}

export function FraudAlertsPage(): JSX.Element {
  const [tab, setTab] = useState<FraudAlertStatus | ''>('NEW');
  const [disposingId, setDisposingId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const alerts = useQuery({
    queryKey: ['financial-services', 'fraud-alerts', tab],
    queryFn: () => listFraudAlerts({ status: tab || undefined, pageSize: 50 }),
    retry: false,
  });

  const newAlerts = useQuery({
    queryKey: ['financial-services', 'fraud-alerts', 'NEW'],
    queryFn: () => listFraudAlerts({ status: 'NEW', pageSize: 1 }),
    retry: false,
  });

  const disposition = useMutation({
    mutationFn: ({ id, status, notes }: { id: string; status: 'REVIEWED' | 'DISMISSED' | 'CONFIRMED'; notes?: string }) =>
      dispositionFraudAlert(id, { status, notes }),
    onSuccess: async () => {
      setDisposingId(null);
      await queryClient.invalidateQueries({ queryKey: ['financial-services', 'fraud-alerts'] });
    },
  });

  const denied =
    alerts.isError && alerts.error instanceof ApiError &&
    (alerts.error.status === 401 || alerts.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fraud alerts"
        description="Real-time risk queue for financial-services traffic."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <KPICard
          title="Active alerts"
          value={
            newAlerts.isPending ? '…'
              : denied ? '—'
                : (newAlerts.data?.total ?? 0).toLocaleString('en-US')
          }
          hint="New alerts awaiting triage"
          icon={ShieldAlert}
        />
        <KPICard
          title="Total alerts"
          value={
            alerts.isPending ? '…'
              : denied ? '—'
                : tab === ''
                  ? (alerts.data?.total ?? 0).toLocaleString('en-US')
                  : '—'
          }
          hint="status=all"
        />
        <KPICard title="False positive %" value="—" hint="Coming soon" />
      </div>

      {/* Status tabs */}
      <div className="flex gap-2 flex-wrap">
        {STATUS_TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => setTab(t.value)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
              tab === t.value
                ? 'bg-nipost-green text-white border-nipost-green'
                : 'bg-white text-gray-700 border-border hover:bg-gray-50'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {alerts.isPending ? (
        <p className="text-sm text-muted">Loading alerts…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Financials/readFraudAlerts</code> required.
        </DeferredNotice>
      ) : alerts.isError ? (
        <DeferredNotice title="Service temporarily unavailable">
          {(alerts.error as Error).message}
        </DeferredNotice>
      ) : (alerts.data?.items.length ?? 0) === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          No alerts with status {tab || 'any'}.
        </p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <div className="px-4 py-2 bg-gray-50 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Alert queue</h3>
            <span className="text-xs text-muted">{alerts.data?.total ?? 0} total</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-white text-[10px] uppercase tracking-wider text-muted border-b border-border">
              <tr>
                <th className="text-left px-4 py-2">Rule</th>
                <th className="text-left px-4 py-2">Severity</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Created</th>
                <th className="text-left px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {alerts.data?.items.map((alert: FraudAlert) => (
                <tr key={alert.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <p className="text-sm font-semibold text-gray-900">{alert.ruleId}</p>
                    {alert.walletTransactionId && (
                      <p className="text-[10px] font-mono text-muted">{alert.walletTransactionId}</p>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <SeverityBadge severity={alert.severity} />
                  </td>
                  <td className="px-4 py-2">
                    <StatusBadge status={alert.status} />
                  </td>
                  <td className="px-4 py-2 text-xs text-muted">
                    {new Date(alert.createdAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-2">
                    {alert.status === 'NEW' || alert.status === 'REVIEWED' ? (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          aria-label="Dismiss alert"
                          disabled={disposition.isPending && disposingId === alert.id}
                          onClick={() => {
                            setDisposingId(alert.id);
                            disposition.mutate({ id: alert.id, status: 'DISMISSED', notes: 'Dismissed by operator' });
                          }}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded border border-border hover:bg-gray-100 text-gray-700 disabled:opacity-50"
                        >
                          <XCircle className="w-3 h-3" /> Dismiss
                        </button>
                        <button
                          type="button"
                          aria-label="Confirm alert"
                          disabled={disposition.isPending && disposingId === alert.id}
                          onClick={() => {
                            setDisposingId(alert.id);
                            disposition.mutate({ id: alert.id, status: 'CONFIRMED', notes: 'Confirmed by operator' });
                          }}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded border border-red-200 hover:bg-red-50 text-red-700 disabled:opacity-50"
                        >
                          <CheckCircle className="w-3 h-3" /> Confirm
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-muted">
                        {alert.dispositionBy ? `by ${alert.dispositionBy.slice(0, 8)}…` : '—'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
